import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Camera, Search, ArrowLeft, Package, User, Phone, Settings, LogOut, AlertTriangle, Wrench, Users, Plus, Edit2 } from "lucide-react";
import BarcodeScanner from "react-qr-barcode-scanner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Label } from "./components/ui/label";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Alert, AlertDescription } from "./components/ui/alert";

// Import new pages
import UserManagementPage from "./pages/UserManagementPage";
import PasswordUpdatePage from "./pages/PasswordUpdatePage";
import BulkScanPage from "./pages/BulkScanPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_asset-tracker-117/artifacts/sfo88lq2_%E6%B5%8E%E5%8D%97%E6%B2%83%E5%BE%B7LOGO-JPG%E7%89%88.jpg";

// Frontend data cache - persists during session
const dataCache = {
  assets: null,
  assetsTimestamp: 0,
  CACHE_TTL: 60000 // 60 seconds
};

// Auth context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify`);
      setUser({ 
        username: response.data.user, 
        role: response.data.role,
        full_name: response.data.full_name
      });
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user: loggedInUser, role, full_name } = response.data;
      
      localStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ 
        username: loggedInUser, 
        role: role,
        full_name: full_name
      });
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Page Component
const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in  
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Please enter username and password");
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success("Login successful!");
      // Navigation will be handled by useEffect above
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-8">
            <div className="flex justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="Company Logo" 
                className="h-20 w-20 object-contain"
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'%3E%3C/path%3E%3Cpolyline points='9,22 9,12 15,12 15,22'%3E%3C/polyline%3E%3C/svg%3E";
                }}
              />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">沃德工具管理</CardTitle>
            <CardDescription className="text-gray-600">请登录以继续使用系统</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">用户名</Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  type="text"
                  placeholder="请输入用户名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">密码</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="请输入密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button 
                type="submit" 
                data-testid="login-submit-button"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? "登录中..." : "登录"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Home Page Component
const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [manualAssetId, setManualAssetId] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);

  // Prefetch asset list data when home page loads (axios already has token in headers)
  useEffect(() => {
    const prefetchAssets = async () => {
      try {
        // Prefetch in background - axios already has auth header configured
        const token = localStorage.getItem('access_token');
        if (token) {
          await axios.get(`${API}/assets/overview`);
          console.log("Asset list prefetched successfully");
        }
      } catch (error) {
        // Silently fail - this is just prefetch
        console.log("Prefetch skipped or failed");
      }
    };
    
    // Start prefetch after a small delay to not block initial render
    const timer = setTimeout(prefetchAssets, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleScanBarcode = () => {
    navigate("/scan");
  };

  const handleManualLookup = () => {
    if (manualAssetId.trim()) {
      navigate(`/asset/${manualAssetId.trim()}`);
    } else {
      toast.error("请输入资产编号");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header Banner */}
      <div className="bg-slate-900 text-white px-4 sm:px-6 py-4 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 max-w-6xl mx-auto">
          <div className="flex items-center space-x-4">
            {/* Company Logo */}
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-lg bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-700 rounded-lg items-center justify-center" style={{display: 'none'}}>
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-slate-300" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">沃德工具管理</h1>
          </div>
          
          {/* Mobile-responsive button layout */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <span className="text-sm text-slate-300 text-center sm:text-left">欢迎, {user?.username}</span>
            
            {/* Button container with responsive layout */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/assets")}
                className="w-full sm:w-auto text-white border-slate-600 hover:bg-slate-800 text-sm"
              >
                <Search className="w-4 h-4 mr-2" />
                浏览资产
              </Button>
              
              {user?.role === "admin" && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/admin/add-asset")}
                  className="w-full sm:w-auto text-white border-slate-600 hover:bg-slate-800 text-sm"
                >
                  <Package className="w-4 h-4 mr-2" />
                  添加资产
                </Button>
              )}

              {user?.role === "admin" && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/admin/users")}
                  className="w-full sm:w-auto text-white border-slate-600 hover:bg-slate-800 text-sm"
                >
                  <Users className="w-4 h-4 mr-2" />
                  用户管理
                </Button>
              )}

              {user && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/profile/password")}
                  className="w-full sm:w-auto text-white border-slate-600 hover:bg-slate-800 text-sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  更新密码
                </Button>
              )}

              {user?.role !== "user" && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/bulk-scan")}
                  className="w-full sm:w-auto text-white border-slate-600 hover:bg-slate-800 text-sm"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  批量扫描
                </Button>
              )}
              
              <Button 
                variant="ghost" 
                onClick={logout}
                className="w-full sm:w-auto text-white hover:bg-slate-800 text-sm"
              >
                <LogOut className="w-4 h-4 mr-2" />
                退出
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] px-4 sm:px-6 py-8">
        <div className="max-w-md w-full space-y-6 sm:space-y-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">扫描资产条码</h2>
            <p className="text-slate-600 text-sm sm:text-base">使用摄像头扫描条码或手动输入资产编号</p>
          </div>

          <div className="space-y-4">
            {/* Camera Scan Button */}
            <Button 
              onClick={handleScanBarcode}
              className="w-full h-14 sm:h-16 text-base sm:text-lg bg-slate-800 hover:bg-slate-700 text-white shadow-lg"
            >
              <Camera className="w-5 h-5 sm:w-6 sm:h-6 mr-3" />
              摄像头扫描
            </Button>

            {/* Manual Input Toggle */}
            <div className="text-center">
              <Button 
                variant="ghost" 
                onClick={() => setShowManualInput(!showManualInput)}
                className="text-slate-600 hover:text-slate-800 text-sm sm:text-base"
              >
                或手动输入资产编号
              </Button>
            </div>

            {/* Manual Input Section */}
            {showManualInput && (
              <div className="space-y-3 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                <Label htmlFor="assetId" className="text-sm sm:text-base">资产编号</Label>
                <Input
                  id="assetId"
                  placeholder="输入资产编号 (例如: JWAA001)"
                  value={manualAssetId}
                  onChange={(e) => setManualAssetId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualLookup()}
                  className="text-base"
                />
                <Button 
                  onClick={handleManualLookup}
                  className="w-full h-12 bg-slate-600 hover:bg-slate-700 text-base"
                >
                  查找资产
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Asset Details Page
const AssetDetailsPage = ({ assetId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [borrowerName, setBorrowerName] = useState("");
  const [borrowingDepartment, setBorrowingDepartment] = useState("");
  const [supervisorName, setSupervisorName] = useState(user?.full_name || "");
  const [targetReturnDate, setTargetReturnDate] = useState("");
  const [returnSupervisor, setReturnSupervisor] = useState(user?.full_name || "");
  const [maintenanceSupervisor, setMaintenanceSupervisor] = useState(user?.full_name || "");
  const [completeSupervisor, setCompleteSupervisor] = useState(user?.full_name || "");
  const [showBorrowDialog, setShowBorrowDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showDisposeDialog, setShowDisposeDialog] = useState(false);
  const [disposeRemark, setDisposeRemark] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    asset_name: "",
    category: "",
    brand: "",
    model: "",
    purchase_date: "",
    purchase_price: "",
    location: "",
    maintenance_frequency: "",
    description: ""
  });

  // Fetch asset when assetId changes
  useEffect(() => {
    const fetchAssetData = async () => {
      if (!assetId) {
        console.log("AssetId is undefined, skipping fetch");
        return;
      }
      
      try {
        setLoading(true);
        console.log("Fetching asset with ID:", assetId);
        const response = await axios.get(`${API}/assets/${assetId}`);
        setAsset(response.data);
      } catch (error) {
        console.error("Error fetching asset:", error);
        toast.error("资产未找到");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssetData();
  }, [assetId, navigate]);

  // Update default supervisor names when user info changes
  useEffect(() => {
    if (user?.full_name) {
      setSupervisorName(user.full_name);
      setReturnSupervisor(user.full_name);
      setMaintenanceSupervisor(user.full_name);
      setCompleteSupervisor(user.full_name);
    }
  }, [user]);

  const fetchAsset = async () => {
    if (!assetId) {
      console.log("AssetId is undefined, skipping fetch");
      return;
    }
    
    try {
      setLoading(true);
      console.log("Fetching asset with ID:", assetId);
      const response = await axios.get(`${API}/assets/${assetId}`);
      setAsset(response.data);
    } catch (error) {
      console.error("Error fetching asset:", error);
      toast.error("资产未找到");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleBorrow = async () => {
    if (!borrowerName.trim()) {
      toast.error("请输入借用人姓名");
      return;
    }
    if (!borrowingDepartment.trim()) {
      toast.error("请输入借用单位");
      return;
    }
    if (!supervisorName.trim()) {
      toast.error("请输入主管姓名");
      return;
    }
    if (!targetReturnDate) {
      toast.error("请选择预计归还日期");
      return;
    }

    const selectedDate = new Date(targetReturnDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate <= today) {
      toast.error("预计归还日期必须是未来日期");
      return;
    }

    try {
      await axios.post(`${API}/assets/${assetId}/borrow`, {
        borrower_name: borrowerName,
        borrowing_department: borrowingDepartment,
        supervisor_name: supervisorName,
        target_return_date: targetReturnDate
      });
      toast.success("资产借用成功！");
      setShowBorrowDialog(false);
      setBorrowerName("");
      setBorrowingDepartment("");
      setSupervisorName("");
      setTargetReturnDate("");
      fetchAsset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "借用失败");
    }
  };

  const handleReturn = async () => {
    if (!returnSupervisor.trim()) {
      toast.error("请输入归还主管姓名");
      return;
    }

    try {
      await axios.post(`${API}/assets/${assetId}/return`, {
        supervisor_name: returnSupervisor
      });
      toast.success("资产归还成功！");
      setShowReturnDialog(false);
      setReturnSupervisor("");
      fetchAsset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "归还失败");
    }
  };

  const handleCompleteMaintenance = async () => {
    try {
      const response = await axios.post(`${API}/assets/${assetId}/maintenance-complete`);
      toast.success(`维修完成！下次维修日期：${response.data.next_maintenance_date}`);
      setShowCompleteDialog(false);
      fetchAsset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "完成维修失败");
    }
  };

  const handleDisposeAsset = () => {
    console.log("🔍 Dispose button clicked!");
    
    if (!asset?.asset) {
      toast.error("资产信息加载中，请稍后再试");
      return;
    }

    // Open dispose dialog instead of immediate confirmation
    setShowDisposeDialog(true);
  };

  const confirmDisposeAsset = async () => {
    console.log("✅ User confirmed disposal, proceeding...");

    try {
      toast.loading("正在处理报废请求...", { id: "dispose" });
      
      const requestData = {
        supervisor_name: user?.full_name || "管理员",
        notes: disposeRemark.trim() || "资产报废"
      };
      
      console.log("📡 Making API request to:", `${API}/assets/${assetId}/dispose`);
      const response = await axios.post(`${API}/assets/${assetId}/dispose`, requestData);
      
      console.log("✅ API Response:", response.data);
      toast.success("✅ 资产已成功标记为报废！", { id: "dispose" });
      
      // Close dialog and reset state
      setShowDisposeDialog(false);
      setDisposeRemark("");
      
      // Navigate back to assets list after successful disposal
      setTimeout(() => {
        navigate("/assets");
      }, 1500);
      
    } catch (error) {
      console.error("❌ Dispose error:", error);
      toast.error(`❌ 报废失败：${error.response?.data?.detail || error.message || "未知错误"}`, { id: "dispose" });
    }
  };

  const handleEditAsset = () => {
    if (!asset?.asset) {
      toast.error("资产信息加载中，请稍后再试");
      return;
    }

    // Populate edit form with current asset data
    setEditFormData({
      new_asset_id: asset.asset.asset_id || "",
      asset_type: asset.asset.asset_type || "",
      asset_name: asset.asset.asset_name || "",
      category: asset.asset.category || "",
      brand: asset.asset.brand || "",
      model: asset.asset.model || "",
      purchase_date: asset.asset.purchase_date || "",
      purchase_price: asset.asset.purchase_price || "",
      location: asset.asset.location || "",
      maintenance_frequency: asset.asset.maintenance_frequency || "",
      description: asset.asset.description || ""
    });
    
    setShowEditDialog(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const confirmEditAsset = async () => {
    try {
      toast.loading("正在更新资产信息...", { id: "edit" });
      
      // Prepare update data - only include changed fields
      const updateData = {};
      
      // Check if asset_id changed
      if (editFormData.new_asset_id.trim() !== asset.asset.asset_id) {
        updateData.new_asset_id = editFormData.new_asset_id.trim();
      }
      // Check if asset_type changed
      if (editFormData.asset_type.trim() !== (asset.asset.asset_type || "")) {
        updateData.asset_type = editFormData.asset_type.trim();
      }
      if (editFormData.asset_name.trim() !== asset.asset.asset_name) {
        updateData.asset_name = editFormData.asset_name.trim();
      }
      if (editFormData.category !== (asset.asset.category || "")) {
        updateData.category = editFormData.category.trim() || null;
      }
      if (editFormData.brand !== (asset.asset.brand || "")) {
        updateData.brand = editFormData.brand.trim() || null;
      }
      if (editFormData.model !== (asset.asset.model || "")) {
        updateData.model = editFormData.model.trim() || null;
      }
      if (editFormData.location !== (asset.asset.location || "")) {
        updateData.location = editFormData.location.trim() || null;
      }
      if (editFormData.maintenance_frequency !== (asset.asset.maintenance_frequency || "")) {
        updateData.maintenance_frequency = editFormData.maintenance_frequency.trim() || null;
      }
      if (editFormData.description !== (asset.asset.description || "")) {
        updateData.description = editFormData.description.trim() || null;
      }
      if (editFormData.purchase_date !== (asset.asset.purchase_date || "")) {
        updateData.purchase_date = editFormData.purchase_date || null;
      }
      if (editFormData.purchase_price !== (asset.asset.purchase_price || "")) {
        const price = parseFloat(editFormData.purchase_price);
        updateData.purchase_price = isNaN(price) ? null : price;
      }
      
      const response = await axios.put(`${API}/admin/assets/${assetId}`, updateData);
      
      toast.success("✅ 资产信息更新成功！", { id: "edit" });
      
      // Close dialog
      setShowEditDialog(false);
      
      // If asset_id was changed, navigate to new URL
      if (updateData.new_asset_id) {
        navigate(`/asset/${updateData.new_asset_id}`);
      } else {
        // Just refresh current asset data
        fetchAsset();
      }
      
    } catch (error) {
      console.error("❌ Edit error:", error);
      
      let errorMessage = "更新资产失败";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`❌ ${errorMessage}`, { id: "edit" });
    }
  };

  const handleMaintenance = async () => {
    if (!maintenanceSupervisor.trim()) {
      toast.error("请输入维修主管姓名");
      return;
    }

    try {
      await axios.post(`${API}/assets/${assetId}/maintenance`, {
        supervisor_name: maintenanceSupervisor
      });
      toast.success("维修状态设置成功！");
      setShowMaintenanceDialog(false);
      setMaintenanceSupervisor("");
      fetchAsset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "设置维修状态失败");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mx-auto"></div>
          <p className="mt-4 text-slate-600">加载资产详情...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/assets")}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            所有资产
          </Button>
          <div className="flex items-center space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-lg font-semibold">资产详情</h1>
          </div>
        </div>
      </div>

      {/* Asset Details */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{asset.asset.asset_type}</CardTitle>
                <CardDescription className="text-lg mt-1">编号: {asset.asset.asset_id}</CardDescription>
              </div>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                asset.asset.current_status === "可使用" ? "bg-green-100 text-green-800 border-green-200" :
                asset.asset.current_status === "已借出" ? "bg-red-100 text-red-800 border-red-200" :
                asset.asset.current_status === "状态待确认" ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                asset.asset.current_status === "维修中" ? "bg-gray-100 text-gray-800 border-gray-200" :
                "bg-slate-100 text-slate-800 border-slate-200"
              }`}>
                {asset.asset.current_status}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Maintenance Alert - Only show for assets NOT under maintenance */}
            {asset.maintenance_due && asset.asset.current_status !== "维修中" && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  维修提醒: 该资产需要进行维修保养
                  {asset.days_until_maintenance < 0 && ` (已逾期 ${Math.abs(asset.days_until_maintenance)} 天)`}
                </AlertDescription>
              </Alert>
            )}

            {/* Maintenance Recommendation - Only show for assets NOT under maintenance */}
            {asset.maintenance_due && asset.asset.current_status !== "维修中" && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  *建议送检维修
                </AlertDescription>
              </Alert>
            )}

            {/* Asset Information */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-slate-800 mb-3">资产信息</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-slate-700">资产编号: </span>
                    <span className="text-sm text-slate-600">{asset.asset.asset_id}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">资产名称: </span>
                    <span className="text-sm text-slate-600">{asset.asset.asset_name || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">资产类型: </span>
                    <span className="text-sm text-slate-600">{asset.asset.asset_type}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">分类: </span>
                    <span className="text-sm text-slate-600">{asset.asset.category || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">品牌: </span>
                    <span className="text-sm text-slate-600">{asset.asset.brand || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">型号: </span>
                    <span className="text-sm text-slate-600">{asset.asset.model || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">位置: </span>
                    <span className="text-sm text-slate-600">{asset.asset.location || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">制造商: </span>
                    <span className="text-sm text-slate-600">{asset.asset.manufacturer || "N/A"}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-slate-700">购买日期: </span>
                    <span className="text-sm text-slate-600">
                      {asset.asset.purchase_date ? new Date(asset.asset.purchase_date).toLocaleDateString('zh-CN') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">购买价格: </span>
                    <span className="text-sm text-slate-600">
                      {asset.asset.purchase_price ? `¥${asset.asset.purchase_price}` : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">维修频率: </span>
                    <span className="text-sm text-slate-600">{asset.asset.maintenance_frequency || "N/A"}</span>
                  </div>
                  {asset.asset.next_maintenance_date && (
                    <div>
                      <span className="text-sm font-medium text-slate-700">下次维修日期: </span>
                      <span className="text-sm text-slate-600">
                        {new Date(asset.asset.next_maintenance_date).toLocaleDateString('zh-CN')}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-slate-700">精度: </span>
                    <span className="text-sm text-slate-600">{asset.asset.accuracy || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">量程: </span>
                    <span className="text-sm text-slate-600">{asset.asset.range_value || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">防水等级: </span>
                    <span className="text-sm text-slate-600">{asset.asset.waterproof_grade || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">原始编号: </span>
                    <span className="text-sm text-slate-600">{asset.asset.original_number || "N/A"}</span>
                  </div>
                </div>
              </div>
              
              {/* Technical Specifications */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">技术规格</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <span className="text-xs text-slate-500 block">防震</span>
                    <span className={`inline-block px-2 py-1 rounded text-xs ${
                      asset.asset.is_shockproof === 'Y' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {asset.asset.is_shockproof === 'Y' ? '是' : '否'}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs text-slate-500 block">防尘</span>
                    <span className={`inline-block px-2 py-1 rounded text-xs ${
                      asset.asset.is_dustproof === 'Y' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {asset.asset.is_dustproof === 'Y' ? '是' : '否'}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs text-slate-500 block">数据传输</span>
                    <span className={`inline-block px-2 py-1 rounded text-xs ${
                      asset.asset.is_data_transferable === 'Y' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {asset.asset.is_data_transferable === 'Y' ? '是' : '否'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {asset.asset.description && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">其他特征描述</h4>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{asset.asset.description}</p>
                </div>
              )}
            </div>

            {/* Historical Information - New Section */}
            {asset.history && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-3">历史记录</h3>
                <div className="space-y-4">
                  
                  {/* Last Borrowing Info */}
                  {asset.history.last_borrow_info && (
                    <div className="border-l-4 border-blue-400 pl-3">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">上次借用信息</h4>
                      <div className="space-y-1">
                        <div>
                          <span className="text-xs text-slate-500">借用人: </span>
                          <span className="text-xs text-slate-600">{asset.history.last_borrow_info.borrower_name}</span>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">审批经理: </span>
                          <span className="text-xs text-slate-600">{asset.history.last_borrow_info.approved_by}</span>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">借用日期: </span>
                          <span className="text-xs text-slate-600">
                            {new Date(asset.history.last_borrow_info.borrow_date).toLocaleDateString('zh-CN')}
                          </span>
                        </div>
                        {asset.history.last_borrow_info.actual_return_date && (
                          <div>
                            <span className="text-xs text-slate-500">实际归还日期: </span>
                            <span className="text-xs text-slate-600">
                              {new Date(asset.history.last_borrow_info.actual_return_date).toLocaleDateString('zh-CN')}
                            </span>
                          </div>
                        )}
                        
                        {/* Merged Return Confirmation Info */}
                        {asset.history.last_return_confirmation && (
                          <>
                            <hr className="my-2 border-slate-300" />
                            <div className="pt-1">
                              <span className="text-xs font-medium text-slate-600">状态确认信息:</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-500">确认经理: </span>
                              <span className="text-xs text-slate-600">{asset.history.last_return_confirmation.confirmed_by}</span>
                            </div>
                            <div>
                              <span className="text-xs text-slate-500">确认日期: </span>
                              <span className="text-xs text-slate-600">
                                {new Date(asset.history.last_return_confirmation.confirmation_date).toLocaleDateString('zh-CN')}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Last Maintenance Approval */}
                  {asset.history.last_maintenance_approval && (
                    <div className="border-l-4 border-orange-400 pl-3">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">上次维修审批</h4>
                      <div className="space-y-1">
                        <div>
                          <span className="text-xs text-slate-500">审批经理: </span>
                          <span className="text-xs text-slate-600">{asset.history.last_maintenance_approval.approved_by}</span>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500">维修日期: </span>
                          <span className="text-xs text-slate-600">
                            {new Date(asset.history.last_maintenance_approval.maintenance_date).toLocaleDateString('zh-CN')}
                          </span>
                        </div>
                        {asset.history.last_maintenance_approval.notes && (
                          <div>
                            <span className="text-xs text-slate-500">备注: </span>
                            <span className="text-xs text-slate-600">{asset.history.last_maintenance_approval.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Current Borrower Information - Enhanced */}
            {asset.asset.current_status === "已借出" && asset.history && asset.history.current_borrower_info && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-slate-800 mb-3">当前借用信息</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-700 font-medium">{asset.history.current_borrower_info.borrower_name}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    借用时间: {new Date(asset.history.current_borrower_info.borrow_date).toLocaleDateString('zh-CN')}
                  </div>
                  {asset.history.current_borrower_info.borrowing_department && (
                    <div className="text-sm text-slate-500">
                      借用单位: {asset.history.current_borrower_info.borrowing_department}
                    </div>
                  )}
                  {asset.history.current_borrower_info.target_return_date && (
                    <div className="text-sm text-slate-500">
                      预计归还: {new Date(asset.history.current_borrower_info.target_return_date).toLocaleDateString('zh-CN')}
                    </div>
                  )}
                  <div className="text-sm text-slate-500">
                    审批经理: {asset.history.current_borrower_info.approved_by}
                  </div>
                </div>
              </div>
            )}
            
            {/* Fallback to old current borrower format if new format not available */}
            {asset.asset.current_status === "已借出" && asset.current_borrower && (!asset.history || !asset.history.current_borrower_info) && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-slate-800 mb-3">当前借用人</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-700">{asset.current_borrower.borrower_name}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    借用时间: {new Date(asset.current_borrower.borrow_date).toLocaleDateString('zh-CN')}
                  </div>
                  {asset.current_borrower.target_return_date && (
                    <div className="text-sm text-slate-500">
                      预计归还: {new Date(asset.current_borrower.target_return_date).toLocaleDateString('zh-CN')}
                    </div>
                  )}
                  <div className="text-sm text-slate-500">
                    主管: {asset.current_borrower.supervisor_name}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* Admin-only actions */}
              {user?.role === "admin" && (
                <div className="flex space-x-2 w-full">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleEditAsset()}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    编辑资产
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={() => handleDisposeAsset()}
                  >
                    报废资产
                  </Button>
                </div>
              )}
              
              {/* All non-user roles can perform borrow/return/maintenance actions */}
              {user?.role !== "user" && (
                <div className="flex space-x-4">
                  {asset.asset.current_status === "可使用" ? (
                <div className="flex space-x-2 w-full">
                  <Dialog open={showBorrowDialog} onOpenChange={setShowBorrowDialog}>
                    <DialogTrigger asChild>
                      <Button className="flex-1 bg-green-600 hover:bg-green-700">
                        借用资产
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>借用资产</DialogTitle>
                        <DialogDescription>
                          请提供借用人和主管详细信息 - {asset.asset.asset_type}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="borrowerName">借用人姓名 *</Label>
                          <Input
                            id="borrowerName"
                            placeholder="输入完整姓名"
                            value={borrowerName}
                            onChange={(e) => setBorrowerName(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="borrowingDepartment">借用单位 *</Label>
                          <Input
                            id="borrowingDepartment"
                            placeholder="输入借用单位"
                            value={borrowingDepartment}
                            onChange={(e) => setBorrowingDepartment(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="supervisorName">主管姓名 *</Label>
                          <Input
                            id="supervisorName"
                            placeholder="输入主管姓名"
                            value={supervisorName}
                            onChange={(e) => setSupervisorName(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="targetReturnDate">预计归还日期 *</Label>
                          <Input
                            id="targetReturnDate"
                            type="date"
                            value={targetReturnDate}
                            onChange={(e) => setTargetReturnDate(e.target.value)}
                            min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          />
                        </div>
                        <div className="flex space-x-4">
                          <Button 
                            onClick={handleBorrow}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            确认借用
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => setShowBorrowDialog(false)}
                            className="flex-1"
                          >
                            取消
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50">
                        <Wrench className="w-4 h-4 mr-2" />
                        维修
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>设置维修状态</DialogTitle>
                        <DialogDescription>
                          请提供维修主管信息 - {asset.asset.asset_type}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="maintenanceSupervisor">维修主管姓名 *</Label>
                          <Input
                            id="maintenanceSupervisor"
                            placeholder="输入维修主管姓名"
                            value={maintenanceSupervisor}
                            onChange={(e) => setMaintenanceSupervisor(e.target.value)}
                          />
                        </div>
                        <div className="flex space-x-4">
                          <Button 
                            onClick={handleMaintenance}
                            className="flex-1 bg-orange-600 hover:bg-orange-700"
                          >
                            确认维修
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => setShowMaintenanceDialog(false)}
                            className="flex-1"
                          >
                            取消
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : asset.asset.current_status === "已借出" ? (
                <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 bg-blue-600 hover:bg-blue-700">
                      归还资产
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>归还资产</DialogTitle>
                      <DialogDescription>
                        请提供归还主管详细信息 - {asset.asset.asset_type}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="returnSupervisor">归还主管姓名 *</Label>
                        <Input
                          id="returnSupervisor"
                          placeholder="输入主管姓名"
                          value={returnSupervisor}
                          onChange={(e) => setReturnSupervisor(e.target.value)}
                        />
                      </div>
                      <div className="flex space-x-4">
                        <Button 
                          onClick={handleReturn}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                          确认归还
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => setShowReturnDialog(false)}
                          className="flex-1"
                        >
                          取消
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : asset.asset.current_status === "维修中" && (user.role === "admin" || user.role === "manager") ? (
                <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 bg-green-600 hover:bg-green-700">
                      完成维修
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>完成维修</DialogTitle>
                      <DialogDescription>
                        确认完成维修 - {asset.asset.asset_name}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="text-sm text-slate-600">
                        完成维修后，系统将自动更新下次维修日期。
                      </div>
                      <div className="flex space-x-4">
                        <Button 
                          onClick={handleCompleteMaintenance}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          确认完成
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={() => setShowCompleteDialog(false)}
                          className="flex-1"
                        >
                          取消
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : asset.asset.current_status === "状态待确认" ? (
                <Button 
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/assets/${assetId}/confirm-return`, {
                        supervisor_name: user?.full_name || "系统管理员"
                      });
                      toast.success("状态确认成功！");
                      fetchAsset();
                    } catch (error) {
                      toast.error("确认失败");
                    }
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  确认状态
                </Button>
              ) : asset.asset.current_status === "维修中" ? (
                <div className="flex-1 bg-yellow-100 border border-yellow-300 rounded-lg p-4 text-center">
                  <p className="text-yellow-800 font-medium">资产维修中</p>
                  <p className="text-yellow-600 text-sm">请联系管理员了解更多信息</p>
                </div>
              ) : null}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Dispose Asset Dialog */}
      <Dialog open={showDisposeDialog} onOpenChange={setShowDisposeDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="space-y-4">
            <div className="text-center space-y-2">
              <div className="text-6xl">⚠️</div>
              <h2 className="text-xl font-bold text-red-600">确认报废资产</h2>
              
              <div className="bg-gray-50 p-3 rounded-lg text-left space-y-1">
                <p><span className="font-medium">编号：</span>{asset?.asset?.asset_id}</p>
                <p><span className="font-medium">名称：</span>{asset?.asset?.asset_name}</p>
                <p><span className="font-medium">类型：</span>{asset?.asset?.asset_type}</p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 font-medium">⚠️ 警告：此操作将永久标记该资产为报废状态，无法撤销！</p>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="disposeRemark" className="text-sm font-medium text-gray-700">
                报废备注 <span className="text-gray-500">(可选)</span>
              </label>
              <textarea
                id="disposeRemark"
                value={disposeRemark}
                onChange={(e) => setDisposeRemark(e.target.value)}
                className="w-full p-3 border rounded-md resize-none"
                rows="3"
                placeholder="请输入报废原因或备注信息..."
              />
            </div>

            <div className="flex space-x-2">
              <Button 
                onClick={confirmDisposeAsset}
                variant="destructive" 
                className="flex-1"
              >
                确认报废
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDisposeDialog(false);
                  setDisposeRemark("");
                }}
                className="flex-1"
              >
                取消
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>编辑资产信息</DialogTitle>
            <DialogDescription>
              修改资产信息（包括资产编号和资产类型）
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Asset ID and Type - Now Editable */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_new_asset_id">资产编号 *</Label>
                <Input
                  id="edit_new_asset_id"
                  name="new_asset_id"
                  value={editFormData.new_asset_id || ""}
                  onChange={handleEditInputChange}
                  placeholder="输入资产编号"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">修改后将更新所有关联记录</p>
              </div>
              <div>
                <Label htmlFor="edit_asset_type">资产类型 *</Label>
                <Input
                  id="edit_asset_type"
                  name="asset_type"
                  value={editFormData.asset_type || ""}
                  onChange={handleEditInputChange}
                  placeholder="输入资产类型"
                />
              </div>
            </div>

            {/* Editable fields */}
            <div>
              <Label htmlFor="edit_asset_name">资产名称 *</Label>
              <Input
                id="edit_asset_name"
                name="asset_name"
                value={editFormData.asset_name}
                onChange={handleEditInputChange}
                placeholder="输入资产名称"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_category">类别</Label>
                <Input
                  id="edit_category"
                  name="category"
                  value={editFormData.category}
                  onChange={handleEditInputChange}
                  placeholder="输入类别"
                />
              </div>
              <div>
                <Label htmlFor="edit_brand">品牌</Label>
                <Input
                  id="edit_brand"
                  name="brand"
                  value={editFormData.brand}
                  onChange={handleEditInputChange}
                  placeholder="输入品牌"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_model">型号</Label>
                <Input
                  id="edit_model"
                  name="model"
                  value={editFormData.model}
                  onChange={handleEditInputChange}
                  placeholder="输入型号"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_purchase_date">购买日期</Label>
                <Input
                  id="edit_purchase_date"
                  name="purchase_date"
                  type="date"
                  value={editFormData.purchase_date}
                  onChange={handleEditInputChange}
                />
              </div>
              <div>
                <Label htmlFor="edit_purchase_price">购买价格</Label>
                <Input
                  id="edit_purchase_price"
                  name="purchase_price"
                  type="number"
                  step="0.01"
                  value={editFormData.purchase_price}
                  onChange={handleEditInputChange}
                  placeholder="输入购买价格"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_location">位置</Label>
                <Input
                  id="edit_location"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditInputChange}
                  placeholder="输入资产位置"
                />
              </div>
              <div>
                <Label htmlFor="edit_maintenance_frequency">维护频率</Label>
                <Input
                  id="edit_maintenance_frequency"
                  name="maintenance_frequency"
                  value={editFormData.maintenance_frequency}
                  onChange={handleEditInputChange}
                  placeholder="如: 90 days, 1 year"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_description">描述</Label>
              <textarea
                id="edit_description"
                name="description"
                value={editFormData.description}
                onChange={handleEditInputChange}
                className="w-full p-3 border rounded-md resize-none"
                rows="3"
                placeholder="输入资产描述"
              />
            </div>

            <div className="flex space-x-2 pt-4">
              <Button 
                onClick={confirmEditAsset}
                className="flex-1"
              >
                保存更改
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                }}
                className="flex-1"
              >
                取消
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Barcode Scanner Page
const ScanPage = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(true);

  const handleScan = (err, result) => {
    if (result && result.text) {
      toast.success(`扫描到: ${result.text}`);
      navigate(`/asset/${result.text}`);
    }
  };

  const handleError = (err) => {
    console.error("扫描器错误:", err);
    toast.error("摄像头访问失败，请使用手动输入");
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-slate-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回
          </Button>
          <img 
            src={LOGO_URL} 
            alt="Company Logo" 
            className="h-8 w-8 object-contain rounded bg-white p-1"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
            <Package className="w-4 h-4 text-slate-300" />
          </div>
        </div>
      </div>

      {/* Scanner Area */}
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-6">
        <div className="max-w-md w-full">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="space-y-4 pt-6">
              {scanning ? (
                <div className="aspect-square rounded-lg overflow-hidden">
                  <BarcodeScanner
                    width="100%"
                    height="100%" 
                    onUpdate={handleScan}
                    onError={handleError}
                  />
                </div>
              ) : (
                <div className="aspect-square bg-slate-700 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-600">
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-400">扫描器已停止</p>
                    <Button 
                      onClick={() => setScanning(true)}
                      className="mt-2 bg-slate-600 hover:bg-slate-500"
                    >
                      重启扫描器
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Button 
                  onClick={() => setScanning(!scanning)}
                  variant="outline"
                  className="w-full text-white border-slate-600 hover:bg-slate-700"
                >
                  {scanning ? "停止扫描" : "开始扫描"}
                </Button>
                
                <Button 
                  onClick={() => navigate("/")}
                  variant="outline"
                  className="w-full text-white border-slate-600 hover:bg-slate-700"
                >
                  使用手动输入
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Asset List Page
const AssetListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groupedAssets, setGroupedAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    asset_id: "",
    asset_name: "", 
    location: "",
    current_status: "",
    expected_return_date: "",
    next_maintenance_date: "",
    borrowing_department: "",
    category: "",
    waterproof_grade: "",
    accuracy: "",
    range_value: "",
    is_shockproof: "",
    is_dustproof: "",
    is_data_transferable: "",
    manufacturer: "",
    brand: "",
    model: "",
    description: ""
  });
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [expandedTypes, setExpandedTypes] = useState({});
  const [pagination, setPagination] = useState({ total_count: 0 });
  
  // Request sequence number to prevent race conditions
  const requestIdRef = React.useRef(0);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch assets on mount and when search/filter changes
  useEffect(() => {
    const fetchData = async () => {
      const currentRequestId = ++requestIdRef.current;
      const now = Date.now();
      
      // Check frontend cache first (for default view only)
      if (!debouncedSearchTerm && !statusFilter && dataCache.assets && 
          (now - dataCache.assetsTimestamp) < dataCache.CACHE_TTL) {
        console.log("Using frontend cached assets");
        const grouped = {};
        for (const item of dataCache.assets) {
          const assetType = item.asset?.asset_type || '未知类型';
          if (!grouped[assetType]) {
            grouped[assetType] = [];
          }
          item.asset.maintenance_status = item.maintenance_status;
          grouped[assetType].push(item.asset);
        }
        setGroupedAssets(grouped);
        setPagination({ total_count: dataCache.assets.length });
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // For initial load without filters, use quick API first
        if (!debouncedSearchTerm && !statusFilter) {
          try {
            // Try quick load first
            const quickResponse = await axios.get(`${API}/assets/overview/quick`);
            
            if (currentRequestId !== requestIdRef.current) return;
            
            const quickData = quickResponse.data;
            if (quickData.is_partial) {
              // Show partial data immediately
              const quickAssets = quickData.assets || [];
              const grouped = {};
              for (const item of quickAssets) {
                const assetType = item.asset?.asset_type || '未知类型';
                if (!grouped[assetType]) {
                  grouped[assetType] = [];
                }
                item.asset.maintenance_status = item.maintenance_status;
                grouped[assetType].push(item.asset);
              }
              setGroupedAssets(grouped);
              setPagination({ total_count: quickData.total_count });
              setLoading(false);
              
              // Load full data in background (non-blocking)
              axios.get(`${API}/assets/overview`).then(fullResponse => {
                if (currentRequestId !== requestIdRef.current) return;
                
                const fullData = fullResponse.data;
                const fullAssets = fullData.assets || [];
                
                // Cache and update with full data
                dataCache.assets = fullAssets;
                dataCache.assetsTimestamp = Date.now();
                
                const fullGrouped = {};
                for (const item of fullAssets) {
                  const assetType = item.asset?.asset_type || '未知类型';
                  if (!fullGrouped[assetType]) {
                    fullGrouped[assetType] = [];
                  }
                  item.asset.maintenance_status = item.maintenance_status;
                  fullGrouped[assetType].push(item.asset);
                }
                setGroupedAssets(fullGrouped);
                setPagination({ total_count: fullData.total_count });
              }).catch(err => console.log("Background full load failed:", err));
              
              return;
            }
          } catch (quickError) {
            console.log("Quick load failed, falling back to full load");
          }
        }

        // Regular full load
        const response = await axios.get(`${API}/assets/overview`, {
          params: { 
            search: debouncedSearchTerm || undefined,
            status: statusFilter || undefined
          }
        });

        // Check if this response is still relevant
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        const responseData = response.data;
        const assetsArray = responseData.assets || [];
        const totalCount = responseData.total_count || assetsArray.length;
        
        // Cache default view data in frontend
        if (!debouncedSearchTerm && !statusFilter) {
          dataCache.assets = assetsArray;
          dataCache.assetsTimestamp = now;
        }
        
        setPagination({ total_count: totalCount });
        
        // Optimized grouping - reduce object spread operations
        const grouped = {};
        for (let i = 0; i < assetsArray.length; i++) {
          const item = assetsArray[i];
          const assetType = item.asset?.asset_type || '未知类型';
          if (!grouped[assetType]) {
            grouped[assetType] = [];
          }
          // Direct assignment instead of spread
          item.asset.maintenance_status = item.maintenance_status;
          grouped[assetType].push(item.asset);
        }

        setGroupedAssets(grouped);
      } catch (error) {
        if (currentRequestId === requestIdRef.current) {
          toast.error("加载资产失败");
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [debouncedSearchTerm, statusFilter]);

  const toggleExpanded = (assetType) => {
    setExpandedTypes(prev => ({
      ...prev,
      [assetType]: !prev[assetType]
    }));
  };

  // Refresh function to trigger re-fetch
  const refreshAssets = () => {
    // Force a re-fetch by toggling a state (debouncedSearchTerm will trigger useEffect)
    setDebouncedSearchTerm(prev => prev === "" ? " " : prev.trim());
    setTimeout(() => setDebouncedSearchTerm(searchTerm), 100);
  };

  const handleConfirmReturn = async (assetId) => {
    try {
      await axios.post(`${API}/assets/${assetId}/confirm-return`, {
        supervisor_name: user?.full_name || "系统管理员"
      });
      toast.success("状态确认成功！");
      refreshAssets();
    } catch (error) {
      toast.error("确认失败");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="w-full sm:w-auto text-white hover:bg-slate-800 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回主页
          </Button>
          <div className="flex items-center justify-center sm:justify-start space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-base sm:text-lg font-semibold">所有资产</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Search and Filter Bar */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                data-testid="asset-search-input"
                placeholder="按编号、名称、类型、位置搜索资产..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {loading && searchTerm && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400"></div>
                </div>
              )}
            </div>
            
            {/* Status Filter */}
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
              >
                <option value="">所有状态</option>
                <option value="available">可用</option>
                <option value="rented_out">已借出</option>
                <option value="under_maintenance">维修中</option>
                <option value="pending_confirmation">状态待确认</option>
              </select>
            </div>
          </div>
          
          {(searchTerm || statusFilter) && (
            <p className="text-xs text-slate-500">
              搜索 "{searchTerm}" {statusFilter && `(状态: ${
                statusFilter === "available" ? "可用" :
                statusFilter === "rented_out" ? "已借出" :
                statusFilter === "under_maintenance" ? "维修中" :
                statusFilter === "pending_confirmation" ? "状态待确认" : statusFilter
              })`} {loading ? "中..." : `找到 ${pagination.total_count || 0} 个结果`}
            </p>
          )}
        </div>

        {/* Grouped Assets */}
        {loading ? (
          <div className="space-y-6">
            {/* Skeleton Loading - 4 cards for asset types */}
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="shadow-lg animate-pulse">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="h-6 bg-slate-200 rounded w-32"></div>
                    <div className="h-6 bg-slate-200 rounded w-16"></div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                    <div className="h-4 bg-slate-200 rounded w-5/6"></div>
                    <div className="h-4 bg-slate-200 rounded w-4/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedAssets).map(([assetType, assets]) => (
              <Card key={assetType} className="shadow-lg">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50" 
                  onClick={() => toggleExpanded(assetType)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {assetType} ({assets.length})
                    </CardTitle>
                    <Badge variant="outline">{expandedTypes[assetType] ? "收起" : "展开"}</Badge>
                  </div>
                </CardHeader>
                
                {expandedTypes[assetType] && (
                  <CardContent className="p-0">
                    {/* Horizontal scrolling table with sticky scrollbar */}
                    <div className="relative">
                      <div className="overflow-x-auto max-h-[60vh] border rounded-lg" style={{scrollbarWidth: 'thin'}}>
                        <table className="w-full min-w-[1500px]">
                        <thead className="bg-slate-50 sticky top-0">
                          {/* Main Header Row */}
                          <tr>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">
                              <div className="flex items-center justify-between">
                                <span>资产编号</span>
                                <button 
                                  onClick={() => setShowColumnFilters(!showColumnFilters)}
                                  className="text-slate-400 hover:text-slate-600 ml-1"
                                  title={showColumnFilters ? "隐藏筛选" : "显示筛选"}
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" />
                                  </svg>
                                </button>
                              </div>
                            </th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[120px]">资产名称</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">位置</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">资产状态</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[120px]">预计归还日期</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[120px]">下次维修日期</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[120px]">借用单位</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">分类</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">防水等级</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">精度</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">量程</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">是否防震</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">是否防尘</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[100px]">是否数据可传输</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">厂家</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">品牌</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[80px]">型号</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm min-w-[150px]">其他特征描述</th>
                          </tr>
                          
                          {/* Filter Row */}
                          {showColumnFilters && (
                            <tr className="bg-slate-100 border-t border-slate-200">
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选编号..." 
                                  value={columnFilters.asset_id}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, asset_id: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选名称..." 
                                  value={columnFilters.asset_name}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, asset_name: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选位置..." 
                                  value={columnFilters.location}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, location: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <select 
                                  value={columnFilters.current_status}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, current_status: e.target.value}))}
                                  className="h-8 text-xs px-2 border border-slate-300 rounded w-full bg-white"
                                >
                                  <option value="">全部</option>
                                  <option value="可使用">可使用</option>
                                  <option value="已借出">已借出</option>
                                  <option value="已逾期">已逾期</option>
                                  <option value="维修中">维修中</option>
                                  <option value="状态待确认">状态待确认</option>
                                </select>
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选归还日期..." 
                                  value={columnFilters.expected_return_date}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, expected_return_date: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选维修日期..." 
                                  value={columnFilters.next_maintenance_date}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, next_maintenance_date: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选借用单位..." 
                                  value={columnFilters.borrowing_department}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, borrowing_department: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选分类..." 
                                  value={columnFilters.category}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, category: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选防水等级..." 
                                  value={columnFilters.waterproof_grade}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, waterproof_grade: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选精度..." 
                                  value={columnFilters.accuracy}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, accuracy: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选量程..." 
                                  value={columnFilters.range_value}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, range_value: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <select 
                                  value={columnFilters.is_shockproof}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, is_shockproof: e.target.value}))}
                                  className="h-8 text-xs px-1 border border-slate-300 rounded w-full bg-white"
                                >
                                  <option value="">全部</option>
                                  <option value="Y">是</option>
                                  <option value="N">否</option>
                                </select>
                              </th>
                              <th className="py-2 px-4">
                                <select 
                                  value={columnFilters.is_dustproof}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, is_dustproof: e.target.value}))}
                                  className="h-8 text-xs px-1 border border-slate-300 rounded w-full bg-white"
                                >
                                  <option value="">全部</option>
                                  <option value="Y">是</option>
                                  <option value="N">否</option>
                                </select>
                              </th>
                              <th className="py-2 px-4">
                                <select 
                                  value={columnFilters.is_data_transferable}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, is_data_transferable: e.target.value}))}
                                  className="h-8 text-xs px-1 border border-slate-300 rounded w-full bg-white"
                                >
                                  <option value="">全部</option>
                                  <option value="Y">是</option>
                                  <option value="N">否</option>
                                </select>
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选厂家..." 
                                  value={columnFilters.manufacturer}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, manufacturer: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选品牌..." 
                                  value={columnFilters.brand}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, brand: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选型号..." 
                                  value={columnFilters.model}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, model: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                              <th className="py-2 px-4">
                                <Input 
                                  placeholder="筛选描述..." 
                                  value={columnFilters.description}
                                  onChange={(e) => setColumnFilters(prev => ({...prev, description: e.target.value}))}
                                  className="h-8 text-xs"
                                />
                              </th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {(() => {
                            // Get display status - only show return-related status
                            const getDisplayStatus = (status, asset) => {
                              const today = new Date();
                              
                              // Check for overdue returns (highest priority)
                              if (status === "已借出" && asset.expected_return_date) {
                                const expectedReturnDate = new Date(asset.expected_return_date);
                                if (today > expectedReturnDate) {
                                  return "已逾期";
                                }
                              }
                              
                              // Return original status (no maintenance-based status changes)
                              return status;
                            };

                            // Status badge color - only based on return status
                            const getStatusBadgeClass = (status, asset) => {
                              const today = new Date();
                              
                              // Check for overdue returns (red color)
                              if (status === "已借出" && asset.expected_return_date) {
                                const expectedReturnDate = new Date(asset.expected_return_date);
                                if (today > expectedReturnDate) {
                                  return "bg-red-100 text-red-800 border-red-200";
                                }
                              }
                              
                              // Status badge color - only based on return/borrow status (NOT maintenance)
                              switch(status) {
                                case "可使用":
                                  return "bg-green-100 text-green-800 border-green-200";
                                case "已借出":
                                  return "bg-blue-100 text-blue-800 border-blue-200";
                                case "状态待确认":
                                  return "bg-yellow-100 text-yellow-800 border-yellow-200";
                                case "维修中":
                                  return "bg-gray-100 text-gray-800 border-gray-200";
                                default:
                                  return "bg-slate-100 text-slate-800 border-slate-200";
                              }
                            };

                            // Apply column filters
                            const filteredAssets = assets.filter(asset => {
                              // Check each column filter
                              if (columnFilters.asset_id && !asset.asset_id.toLowerCase().includes(columnFilters.asset_id.toLowerCase())) return false;
                              if (columnFilters.asset_name && !asset.asset_name?.toLowerCase().includes(columnFilters.asset_name.toLowerCase())) return false;
                              if (columnFilters.location && !asset.location?.toLowerCase().includes(columnFilters.location.toLowerCase())) return false;
                              
                              // Status filter with display status logic
                              if (columnFilters.current_status) {
                                const displayStatus = getDisplayStatus(asset.current_status, asset);
                                if (displayStatus !== columnFilters.current_status) return false;
                              }
                              
                              if (columnFilters.next_maintenance_date && !asset.next_maintenance_date?.includes(columnFilters.next_maintenance_date)) return false;
                              if (columnFilters.expected_return_date && !asset.expected_return_date?.includes(columnFilters.expected_return_date)) return false;
                              if (columnFilters.borrowing_department && !asset.borrowing_department?.toLowerCase().includes(columnFilters.borrowing_department.toLowerCase())) return false;
                              if (columnFilters.category && !asset.category?.toLowerCase().includes(columnFilters.category.toLowerCase())) return false;
                              if (columnFilters.waterproof_grade && !asset.waterproof_grade?.toLowerCase().includes(columnFilters.waterproof_grade.toLowerCase())) return false;
                              if (columnFilters.accuracy && !asset.accuracy?.toLowerCase().includes(columnFilters.accuracy.toLowerCase())) return false;
                              if (columnFilters.range_value && !asset.range_value?.toLowerCase().includes(columnFilters.range_value.toLowerCase())) return false;
                              if (columnFilters.is_shockproof && asset.is_shockproof !== columnFilters.is_shockproof) return false;
                              if (columnFilters.is_dustproof && asset.is_dustproof !== columnFilters.is_dustproof) return false;
                              if (columnFilters.is_data_transferable && asset.is_data_transferable !== columnFilters.is_data_transferable) return false;
                              if (columnFilters.manufacturer && !asset.manufacturer?.toLowerCase().includes(columnFilters.manufacturer.toLowerCase())) return false;
                              if (columnFilters.brand && !asset.brand?.toLowerCase().includes(columnFilters.brand.toLowerCase())) return false;
                              if (columnFilters.model && !asset.model?.toLowerCase().includes(columnFilters.model.toLowerCase())) return false;
                              if (columnFilters.description && !asset.description?.toLowerCase().includes(columnFilters.description.toLowerCase())) return false;
                              
                              return true;
                            });

                            return filteredAssets.map((asset) => {
                              // Calculate maintenance due status
                              const today = new Date();
                              const nextMaintenanceDate = asset.next_maintenance_date ? new Date(asset.next_maintenance_date) : null;
                              
                              // Calculate days until maintenance
                              let daysUntilMaintenance = null;
                              let isMaintenanceOverdue = false;
                              let isMaintenanceDueSoon = false;
                              
                              if (nextMaintenanceDate) {
                                daysUntilMaintenance = Math.ceil((nextMaintenanceDate - today) / (1000 * 60 * 60 * 24));
                                isMaintenanceOverdue = daysUntilMaintenance < 0;
                                isMaintenanceDueSoon = daysUntilMaintenance >= 0 && daysUntilMaintenance <= 7;
                              }
                              
                              // Calculate overdue return status from backend data
                              const isOverdueReturn = asset.is_overdue_return || false;
                              
                              // Determine row highlighting:
                              // - Red: maintenance date overdue OR return date overdue
                              // - Yellow: maintenance within 7 days
                              // - White: normal
                              let rowBgClass = "bg-white hover:bg-slate-50";
                              if (isMaintenanceOverdue || isOverdueReturn) {
                                rowBgClass = "bg-red-100 hover:bg-red-200";
                              } else if (isMaintenanceDueSoon && asset.current_status !== "维修中") {
                                rowBgClass = "bg-yellow-100 hover:bg-yellow-200";
                              }
                              
                              return (
                                <tr 
                                  key={asset.asset_id || asset.id} 
                                  className={`cursor-pointer border-b border-slate-100 ${rowBgClass} transition-colors`}
                                  onClick={() => {
                                    const assetId = asset.asset_id || asset.id;
                                    if (assetId) {
                                      navigate(`/asset/${assetId}`);
                                    }
                                  }}
                                >
                                  <td className="py-3 px-4 font-mono text-sm min-w-[100px] whitespace-nowrap">{asset.asset_id}</td>
                                  <td className="py-3 px-4 font-medium text-sm min-w-[120px] whitespace-nowrap">{asset.asset_name}</td>
                                  <td className="py-3 px-4 text-sm min-w-[100px] whitespace-nowrap">{asset.location || "未指定"}</td>
                                  <td className="py-3 px-4 min-w-[100px]">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusBadgeClass(asset.current_status, asset)}`}>
                                      {getDisplayStatus(asset.current_status, asset)}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm min-w-[120px] whitespace-nowrap">
                                    {asset.expected_return_date ? new Date(asset.expected_return_date).toLocaleDateString('zh-CN') : "-"}
                                  </td>
                                  <td className="py-3 px-4 text-sm min-w-[120px] whitespace-nowrap">
                                    {asset.next_maintenance_date ? new Date(asset.next_maintenance_date).toLocaleDateString('zh-CN') : "-"}
                                  </td>
                                  <td className="py-3 px-4 text-sm min-w-[120px] whitespace-nowrap">{asset.borrowing_department || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] whitespace-nowrap">{asset.category || "未分类"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] whitespace-nowrap">{asset.waterproof_grade || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[100px] whitespace-nowrap">{asset.accuracy || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[100px] whitespace-nowrap">{asset.range_value || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] text-center whitespace-nowrap">{asset.is_shockproof || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] text-center whitespace-nowrap">{asset.is_dustproof || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[100px] text-center whitespace-nowrap">{asset.is_data_transferable || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] whitespace-nowrap">{asset.manufacturer || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] whitespace-nowrap">{asset.brand || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[80px] whitespace-nowrap">{asset.model || "-"}</td>
                                  <td className="py-3 px-4 text-sm min-w-[150px]" title={asset.description && asset.description !== "None" ? asset.description : "-"}>
                                    <div className="max-w-[150px] truncate">
                                      {asset.description && asset.description !== "None" ? asset.description : "-"}
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}

            {/* Asset count summary */}
            {pagination.total_count > 0 && (
              <div className="text-center py-4 text-sm text-slate-500">
                共 {pagination.total_count} 个资产
              </div>
            )}
          </div>
        )}

        {!loading && Object.keys(groupedAssets).length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">未找到资产</p>
            {(searchTerm || statusFilter) && (
              <p className="text-slate-500 text-sm mt-2">
                尝试调整搜索条件或状态筛选
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Add Asset Page
const AddAssetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);
  const [showNewTypeDialog, setShowNewTypeDialog] = useState(false);
  const [useManualAssetId, setUseManualAssetId] = useState(false);
  const [manualAssetId, setManualAssetId] = useState("");
  const [assetIdError, setAssetIdError] = useState("");
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [formData, setFormData] = useState({
    asset_name: "",
    asset_type_id: "",
    category: "",
    waterproof_grade: "",
    accuracy: "",
    range_value: "",
    is_shockproof: "N",
    is_dustproof: "N", 
    is_data_transferable: "N",
    brand: "",
    model: "",
    original_number: "",
    purchase_date: "",
    purchase_price: "",
    calibration_cycle_months: "",
    next_maintenance_date: "",
    other_description: "",
    location: ""
  });
  const [newTypeData, setNewTypeData] = useState({
    desc: "",
    suffix: ""
  });

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("只有管理员可以添加资产");
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === "admin") {
      fetchAssetTypes();
    }
  }, [user]);

  const fetchAssetTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/asset-types`);
      setAssetTypes(response.data);
    } catch (error) {
      toast.error("加载资产类型失败");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewTypeInputChange = (e) => {
    const { name, value } = e.target;
    setNewTypeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Check for duplicate asset ID
  const checkDuplicateAssetId = async (assetId) => {
    if (!assetId.trim()) {
      setAssetIdError("");
      return true;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await axios.get(`${API}/admin/assets/check-duplicate/${encodeURIComponent(assetId.trim())}`);
      if (response.data.exists) {
        setAssetIdError(`资产编号 '${assetId}' 已存在`);
        return false;
      } else {
        setAssetIdError("");
        return true;
      }
    } catch (error) {
      setAssetIdError("检查资产编号失败");
      return false;
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Debounce asset ID check
  useEffect(() => {
    if (useManualAssetId && manualAssetId.trim()) {
      const timer = setTimeout(() => {
        checkDuplicateAssetId(manualAssetId);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setAssetIdError("");
    }
  }, [manualAssetId, useManualAssetId]);

  const handleCreateAssetType = async () => {
    if (!newTypeData.desc.trim() || !newTypeData.suffix.trim()) {
      toast.error("请填写资产类型描述和前缀");
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/asset-types`, newTypeData);
      toast.success("资产类型创建成功！");
      setNewTypeData({ desc: "", suffix: "" });
      setShowNewTypeDialog(false);
      await fetchAssetTypes();
      
      // Auto-select the newly created type
      setFormData(prev => ({
        ...prev,
        asset_type_id: response.data.id
      }));
    } catch (error) {
      if (error.response?.data?.detail === "Asset type description already exists") {
        toast.error("资产类型描述已存在");
      } else if (error.response?.data?.detail === "Asset type suffix already exists") {
        toast.error("资产类型前缀已存在");
      } else {
        toast.error(error.response?.data?.detail || "创建资产类型失败");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim() || !formData.asset_type_id || !formData.purchase_date) {
      toast.error("请填写必填字段");
      return;
    }

    // If using manual asset ID, validate it
    if (useManualAssetId) {
      if (!manualAssetId.trim()) {
        toast.error("请输入资产编号");
        return;
      }
      // Final duplicate check before submit
      const isValid = await checkDuplicateAssetId(manualAssetId);
      if (!isValid) {
        toast.error(assetIdError || "资产编号已存在");
        return;
      }
    }

    setLoading(true);
    try {
      // Process form data for asset_table structure
      const processedData = {
        asset_name: formData.asset_name.trim(),
        asset_type_id: formData.asset_type_id,  // 资产类别编号 from asset_types table
        manual_asset_id: useManualAssetId ? manualAssetId.trim() : null,  // Optional manual asset ID
        category: formData.category || null,
        waterproof_grade: formData.waterproof_grade || null,
        accuracy: formData.accuracy || null,
        range_value: formData.range_value || null,
        is_shockproof: formData.is_shockproof,  // Y/N dropdown
        is_dustproof: formData.is_dustproof,    // Y/N dropdown
        is_data_transferable: formData.is_data_transferable,  // Y/N dropdown
        brand: formData.brand || null,
        model: formData.model || null,
        original_number: formData.original_number || null,
        purchase_date: formData.purchase_date,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        calibration_cycle_months: formData.calibration_cycle_months ? parseInt(formData.calibration_cycle_months) : null,
        next_maintenance_date: formData.next_maintenance_date || null,
        other_description: formData.other_description || null,
        location: formData.location || null
      };

      const response = await axios.post(`${API}/admin/assets/new`, processedData);
      toast.success(`资产创建成功！资产编号: ${response.data.asset_id}`);
      navigate("/assets");
    } catch (error) {
      console.error("Asset creation error:", error);
      
      // Better error handling for validation errors
      let errorMessage = "创建资产失败";
      
      if (error.response?.data?.detail) {
        // Handle validation errors array
        if (Array.isArray(error.response.data.detail)) {
          const firstError = error.response.data.detail[0];
          if (firstError.msg) {
            errorMessage = `验证错误: ${firstError.msg}`;
          } else {
            errorMessage = `验证错误: ${JSON.stringify(firstError)}`;
          }
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回首页
          </Button>
          <div className="flex items-center space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Company Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div className="w-8 h-8 bg-slate-700 rounded items-center justify-center" style={{display: 'none'}}>
              <Package className="w-4 h-4 text-slate-300" />
            </div>
            <h1 className="text-lg font-semibold">添加资产</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>创建新资产</CardTitle>
            <CardDescription>
              {useManualAssetId 
                ? "请填写资产信息和自定义资产编号" 
                : "请填写资产信息，系统将自动生成资产编号"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Manual Asset ID Toggle and Input */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">资产编号</Label>
                  <Button
                    type="button"
                    variant={useManualAssetId ? "default" : "outline"}
                    size="sm"
                    data-testid="toggle-manual-asset-id-button"
                    onClick={() => {
                      setUseManualAssetId(!useManualAssetId);
                      setManualAssetId("");
                      setAssetIdError("");
                    }}
                  >
                    {useManualAssetId ? "使用自动生成" : "手动输入"}
                  </Button>
                </div>
                
                {useManualAssetId ? (
                  <div>
                    <Input
                      placeholder="输入自定义资产编号"
                      data-testid="manual-asset-id-input"
                      value={manualAssetId}
                      onChange={(e) => setManualAssetId(e.target.value)}
                      className={assetIdError ? "border-red-500" : ""}
                    />
                    {checkingDuplicate && (
                      <p className="text-xs text-slate-500 mt-1" data-testid="asset-id-checking">正在检查...</p>
                    )}
                    {assetIdError && (
                      <p className="text-xs text-red-500 mt-1" data-testid="asset-id-error">{assetIdError}</p>
                    )}
                    {!assetIdError && manualAssetId.trim() && !checkingDuplicate && (
                      <p className="text-xs text-green-500 mt-1" data-testid="asset-id-available">✓ 资产编号可用</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    系统将根据所选资产类型自动生成编号
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="asset_name">资产名称 *</Label>
                  <Input
                    id="asset_name"
                    name="asset_name"
                    placeholder="输入资产名称"
                    value={formData.asset_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="asset_type_id">资产类型 *</Label>
                  <div className="flex space-x-2">
                    <select
                      id="asset_type_id"
                      name="asset_type_id"
                      value={formData.asset_type_id}
                      onChange={handleInputChange}
                      className="flex-1 h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                      required
                    >
                      <option value="">选择资产类型</option>
                      {assetTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.desc} ({type.suffix})
                        </option>
                      ))}
                    </select>
                    
                    <Dialog open={showNewTypeDialog} onOpenChange={setShowNewTypeDialog}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="outline" size="sm">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>添加新资产类型</DialogTitle>
                          <DialogDescription>
                            请填写资产类型描述和前缀
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="new_desc">类型描述 *</Label>
                            <Input
                              id="new_desc"
                              name="desc"
                              placeholder="例如: 笔记本电脑"
                              value={newTypeData.desc}
                              onChange={handleNewTypeInputChange}
                            />
                          </div>
                          <div>
                            <Label htmlFor="new_suffix">前缀 (最多10个字符) *</Label>
                            <Input
                              id="new_suffix"
                              name="suffix"
                              placeholder="例如: LAP"
                              value={newTypeData.suffix}
                              onChange={handleNewTypeInputChange}
                              maxLength={10}
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              前缀将用于生成资产编号，例如: LAP0000001
                            </p>
                          </div>
                          <div className="flex space-x-4">
                            <Button 
                              type="button"
                              onClick={handleCreateAssetType}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              创建类型
                            </Button>
                            <Button 
                              type="button"
                              variant="outline" 
                              onClick={() => setShowNewTypeDialog(false)}
                              className="flex-1"
                            >
                              取消
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <div>
                  <Label htmlFor="brand">品牌</Label>
                  <Input
                    id="brand"
                    name="brand"
                    placeholder="输入品牌"
                    value={formData.brand}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Input
                    id="model"
                    name="model"
                    placeholder="输入型号"
                    value={formData.model}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="purchase_date">采购日期 *</Label>
                  <Input
                    id="purchase_date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="purchase_price">采购价格</Label>
                  <Input
                    id="purchase_price"
                    name="purchase_price"
                    type="number"
                    placeholder="输入采购价格"
                    value={formData.purchase_price}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="waterproof_grade">防水等级</Label>
                  <Input
                    id="waterproof_grade"
                    name="waterproof_grade"
                    placeholder="如: IP65"
                    value={formData.waterproof_grade}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="accuracy">精度</Label>
                  <Input
                    id="accuracy"
                    name="accuracy"
                    placeholder="如: ±0.1%"
                    value={formData.accuracy}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="range_value">量程</Label>
                  <Input
                    id="range_value"
                    name="range_value"
                    placeholder="如: 0-100℃"
                    value={formData.range_value}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="is_shockproof">是否防震 *</Label>
                  <select
                    id="is_shockproof"
                    name="is_shockproof"
                    value={formData.is_shockproof}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="N">否 (N)</option>
                    <option value="Y">是 (Y)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="is_dustproof">是否防尘 *</Label>
                  <select
                    id="is_dustproof"
                    name="is_dustproof"
                    value={formData.is_dustproof}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="N">否 (N)</option>
                    <option value="Y">是 (Y)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="is_data_transferable">是否数据可传输 *</Label>
                  <select
                    id="is_data_transferable"
                    name="is_data_transferable"
                    value={formData.is_data_transferable}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="N">否 (N)</option>
                    <option value="Y">是 (Y)</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="original_number">原厂编号</Label>
                  <Input
                    id="original_number"
                    name="original_number"
                    placeholder="输入原厂编号"
                    value={formData.original_number}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="calibration_cycle_months">检定周期 (月)</Label>
                  <Input
                    id="calibration_cycle_months"
                    name="calibration_cycle_months"
                    type="number"
                    placeholder="如: 12"
                    value={formData.calibration_cycle_months}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="next_maintenance_date">下次维修日</Label>
                  <Input
                    id="next_maintenance_date"
                    name="next_maintenance_date"
                    type="date"
                    value={formData.next_maintenance_date}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="location">位置</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="输入位置"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="other_description">其他特征描述</Label>
                <textarea
                  id="other_description"
                  name="other_description"
                  placeholder="输入其他特征描述"
                  value={formData.other_description}
                  onChange={handleInputChange}
                  className="w-full h-20 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white resize-none"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? "创建中..." : "创建资产"}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => navigate("/")}
                  className="flex-1"
                >
                  取消
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

// Main App Component
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/scan" element={
              <ProtectedRoute>
                <ScanPage />
              </ProtectedRoute>
            } />
            <Route path="/asset/:assetId" element={
              <ProtectedRoute>
                <AssetDetailsWrapper />
              </ProtectedRoute>
            } />
            <Route path="/assets" element={
              <ProtectedRoute>
                <AssetListPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/add-asset" element={
              <ProtectedRoute>
                <AddAssetPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <UserManagementWrapper />
              </ProtectedRoute>
            } />
            <Route path="/profile/password" element={
              <ProtectedRoute>
                <PasswordUpdateWrapper />
              </ProtectedRoute>
            } />
            <Route path="/bulk-scan" element={
              <ProtectedRoute>
                <BulkScanWrapper />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Wrapper to pass assetId to AssetDetailsPage
const AssetDetailsWrapper = () => {
  const { assetId } = useParams();
  return <AssetDetailsPage assetId={assetId} />;
};

// Wrapper to pass user to UserManagementPage
const UserManagementWrapper = () => {
  const { user } = useAuth();
  return <UserManagementPage user={user} />;
};

// Wrapper to pass user to PasswordUpdatePage
const PasswordUpdateWrapper = () => {
  const { user } = useAuth();
  return <PasswordUpdatePage user={user} />;
};

// Wrapper to pass user to BulkScanPage
const BulkScanWrapper = () => {
  const { user } = useAuth();
  return <BulkScanPage user={user} />;
};

export default App;